import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import './share.css.js';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { Codicon } from 'vscode/vscode/vs/base/common/codicons';
import { MarkdownString } from 'vscode/vscode/vs/base/common/htmlContent';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { MenuId, registerAction2, Action2, MenuRegistry } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IClipboardService } from 'vscode/vscode/vs/platform/clipboard/common/clipboardService.service';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { EditorResourceAccessor, SideBySideEditor } from 'vscode/vscode/vs/workbench/common/editor';
import { IDialogService } from 'vscode/vscode/vs/platform/dialogs/common/dialogs.service';
import 'vscode/vscode/vs/platform/notification/common/notification';
import { IOpenerService } from 'vscode/vscode/vs/platform/opener/common/opener.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { IWorkspaceContextService } from 'vscode/vscode/vs/platform/workspace/common/workspace.service';
import { WorkspaceFolderCountContext } from 'vscode/vscode/vs/workbench/common/contextkeys';
import { Extensions } from 'vscode/vscode/vs/workbench/common/contributions';
import { ShareProviderCountContext } from './shareService.js';
import { IShareService } from 'vscode/vscode/vs/workbench/contrib/share/common/share.service';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { IProgressService } from 'vscode/vscode/vs/platform/progress/common/progress.service';
import { ICodeEditorService } from 'vscode/vscode/vs/editor/browser/services/codeEditorService';
import { Extensions as Extensions$1 } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { workbenchConfigurationNodeBase } from 'vscode/vscode/vs/workbench/common/configuration';
import { DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import Severity$1 from 'vscode/vscode/vs/base/common/severity';

var ShareWorkbenchContribution_1;
const _moduleId = "vs/workbench/contrib/share/browser/share.contribution";
const targetMenus = [
    MenuId.EditorContextShare,
    MenuId.SCMResourceContextShare,
    MenuId.OpenEditorsContextShare,
    MenuId.EditorTitleContextShare,
    MenuId.MenubarShare,
    MenuId.ExplorerContextShare
];
let ShareWorkbenchContribution = class ShareWorkbenchContribution {
    static { ShareWorkbenchContribution_1 = this; }
    static { this.SHARE_ENABLED_SETTING = 'workbench.experimental.share.enabled'; }
    constructor(shareService, configurationService) {
        this.shareService = shareService;
        this.configurationService = configurationService;
        if (this.configurationService.getValue(ShareWorkbenchContribution_1.SHARE_ENABLED_SETTING)) {
            this.registerActions();
        }
        this.configurationService.onDidChangeConfiguration(e => {
            if (e.affectsConfiguration(ShareWorkbenchContribution_1.SHARE_ENABLED_SETTING)) {
                const settingValue = this.configurationService.getValue(ShareWorkbenchContribution_1.SHARE_ENABLED_SETTING);
                if (settingValue === true && this._disposables === undefined) {
                    this.registerActions();
                }
                else if (settingValue === false && this._disposables !== undefined) {
                    this._disposables?.clear();
                    this._disposables = undefined;
                }
            }
        });
    }
    registerActions() {
        if (!this._disposables) {
            this._disposables = ( (new DisposableStore()));
        }
        this._disposables.add(registerAction2(class ShareAction extends Action2 {
            static { this.ID = 'workbench.action.share'; }
            static { this.LABEL = ( localize2WithPath(_moduleId, 0, 'Share...')); }
            constructor() {
                super({
                    id: ShareAction.ID,
                    title: ShareAction.LABEL,
                    f1: true,
                    icon: Codicon.linkExternal,
                    precondition: ( (ContextKeyExpr.and(
                         (ShareProviderCountContext.notEqualsTo(0)),
                         (WorkspaceFolderCountContext.notEqualsTo(0))
                    ))),
                    keybinding: {
                        weight: 200 ,
                        primary: 512  | 2048  | 49 ,
                    },
                    menu: [
                        { id: MenuId.CommandCenter, order: 1000 }
                    ]
                });
            }
            async run(accessor, ...args) {
                const shareService = accessor.get(IShareService);
                const activeEditor = accessor.get(IEditorService)?.activeEditor;
                const resourceUri = (activeEditor && EditorResourceAccessor.getOriginalUri(activeEditor, { supportSideBySide: SideBySideEditor.PRIMARY }))
                    ?? accessor.get(IWorkspaceContextService).getWorkspace().folders[0].uri;
                const clipboardService = accessor.get(IClipboardService);
                const dialogService = accessor.get(IDialogService);
                const urlService = accessor.get(IOpenerService);
                const progressService = accessor.get(IProgressService);
                const selection = accessor.get(ICodeEditorService).getActiveCodeEditor()?.getSelection() ?? undefined;
                const result = await progressService.withProgress({
                    location: 10 ,
                    detail: ( localizeWithPath(_moduleId, 1, 'Generating link...'))
                }, async () => shareService.provideShare({ resourceUri, selection }, CancellationToken.None));
                if (result) {
                    const uriText = ( (result.toString()));
                    const isResultText = typeof result === 'string';
                    await clipboardService.writeText(uriText);
                    dialogService.prompt({
                        type: Severity$1.Info,
                        message: isResultText ? ( localizeWithPath(_moduleId, 2, 'Copied text to clipboard!')) : ( localizeWithPath(_moduleId, 3, 'Copied link to clipboard!')),
                        custom: {
                            icon: Codicon.check,
                            markdownDetails: [{
                                    markdown: ( (new MarkdownString(`<div aria-label='${uriText}'>${uriText}</div>`, { supportHtml: true }))),
                                    classes: [isResultText ? 'share-dialog-input-text' : 'share-dialog-input-link']
                                }]
                        },
                        cancelButton: ( localizeWithPath(_moduleId, 4, 'Close')),
                        buttons: isResultText ? [] : [{ label: ( localizeWithPath(_moduleId, 5, 'Open Link')), run: () => { urlService.open(result, { openExternal: true }); } }]
                    });
                }
            }
        }));
        const actions = this.shareService.getShareActions();
        for (const menuId of targetMenus) {
            for (const action of actions) {
                this._disposables.add(MenuRegistry.appendMenuItem(menuId, action));
            }
        }
    }
};
ShareWorkbenchContribution = ShareWorkbenchContribution_1 = ( (__decorate([
    ( (__param(0, IShareService))),
    ( (__param(1, IConfigurationService)))
], ShareWorkbenchContribution)));
const workbenchContributionsRegistry = ( (Registry.as(Extensions.Workbench)));
workbenchContributionsRegistry.registerWorkbenchContribution(ShareWorkbenchContribution, 4 );
( (Registry.as(Extensions$1.Configuration))).registerConfiguration({
    ...workbenchConfigurationNodeBase,
    properties: {
        'workbench.experimental.share.enabled': {
            type: 'boolean',
            default: false,
            tags: ['experimental'],
            markdownDescription: ( localizeWithPath(
                _moduleId,
                6,
                "Controls whether to render the Share action next to the command center when {0} is {1}.",
                '`#window.commandCenter#`',
                '`true`'
            )),
            restricted: false,
        }
    }
});
